/**
 * Traffic health thresholds — the console's single copy.
 *
 * These rules decide whether a request-rate reads as normal, warning or
 * critical. There are five graders here — `errorSeverity`,
 * `downstreamSeverity`, `fallbackSeverity`, `slowFirstByteSeverity` and
 * `supplySeverity` — plus `worstSeverity`, which combines verdicts rather
 * than producing one, and they do NOT all come from the same place:
 *
 * - error / fallback / slow-first-byte carry the SAME numbers the gateway
 *   applies in `buildRisks`
 *   (`apps/gateway-worker/src/routes/internal-traffic.ts`) when it triages
 *   the fleet summary;
 * - `downstreamSeverity` reuses the error thresholds but drops the absolute
 *   count arm — see its own comment for the measurements behind that;
 * - the supply pair (`SUPPLY_*`) comes from
 *   `apps/gateway-worker/src/routes/internal-health.ts`, not `buildRisks`,
 *   and answers a different question off a different counter.
 *
 * The point of the shared numbers is that the gateway grades the whole-fleet
 * aggregate, the console grades the individual upstream / SKU / account rows
 * that make it up, and an operator has to be able to trust that a row the
 * console paints red would also have turned the fleet red if the whole fleet
 * looked like that row.
 *
 * Sharing thresholds is necessary but NOT sufficient for that: two callers
 * that feed the same grader different quantities still disagree. Anything
 * grading a tenant-facing failure rate must pass the rate net of gate
 * refusals over the ATTRIBUTABLE denominator (`requests - refused`), which is
 * what `model-nodes.ts` calls `downstreamErrorRate` and what the traffic
 * monitor's 错误率(净) column computes. Netting only the numerator reads low,
 * and worst exactly when refusals dominate.
 *
 * ── KNOWN, DELIBERATE divergence #1: which end of a bound is shown ──────────
 *
 * Where the refusal scalar is present but the per-code split is not — twelve
 * of the thirteen breakdown groups — neither surface can compute the net
 * rate, and they say so in OPPOSITE directions on purpose:
 *
 * - 系统健康透视 prints the LOWER bound (`failed ≥28 (≥1.4%)`,
 *   `supplyVerdict.ts` + `LadderRow.tsx`) and tones it `unknown`/amber. It is
 *   a triage board: its job is to nominate the row worth opening, so it shows
 *   the smallest number it can prove and flags that the truth is worse;
 * - the traffic monitor prints the UPPER bound (`≤ 43.0%`,
 *   `TrafficMonitorPage.tsx`) beside the raw refusal count that is already in
 *   the next column. It is a drill-down table read WITH that column, and a
 *   floor there would contradict the figure sitting next to it.
 *
 * Both are honest ends of the same interval and both carry their marker. What
 * neither may do is grade below the floor: the traffic monitor tones on
 * `floorTone`, the minimum over the interval, for exactly that reason.
 *
 * ── KNOWN, DELIBERATE divergence #2: the count arm ──────────────────────────
 *
 * The traffic monitor grades its rows with `errorSeverity`; 系统健康透视 grades
 * with `downstreamSeverity`. Same thresholds, same quantity — but
 * `errorSeverity` additionally criticals on `ERROR_COUNT_CRITICAL` absolute
 * failures at any denominator, so the two can paint one SKU differently:
 * production 24h, `xai/grok-4.6` at 49/3,552 = 1.4% grades CRITICAL on the
 * traffic monitor and NORMAL on the health map.
 *
 * This is a decision, not an oversight, and it should stay until the
 * condition below is met. Each surface matches the risk vocabulary rendered
 * NEXT TO IT, which is the contradiction an operator actually sees:
 *
 * - the traffic monitor renders the gateway's own `buildRisks` output in its
 *   risk queue, and that rule has the count arm (`criticalCount: 25`). Moving
 *   the page's cells to `downstreamSeverity` would make a row read 正常 while
 *   the risk card beside it says 严重 about the same window;
 * - 系统健康透视 renders no such queue. Its whole job is picking the one row
 *   worth acting on out of a board, and the count arm makes every busy SKU
 *   critical — see `downstreamSeverity`'s own comment for those measurements.
 *
 * Revisit condition: if `buildRisks`
 * (`apps/gateway-worker/src/routes/internal-traffic.ts`) ever moves to the
 * net rate, or drops its absolute-count arm, then BOTH surfaces should move
 * together — the reason for the split is that the gateway has not moved, and
 * that reason disappears with it. Do not "fix" this by aligning one side
 * alone; that trades a documented divergence for an undocumented one.
 *
 * They live here rather than in the one page that used to own them
 * because there are now two consumers — the traffic monitor's breakdown
 * tables and the 系统健康透视 map — and a second copy is how the two
 * surfaces would start disagreeing about what "unhealthy" means while
 * both looked correct in isolation.
 *
 * This module is NOT the authority on the thresholds; the gateway is.
 * If the gateway's numbers move, these must move with them — there is no
 * wire field carrying them, so the duplication is real and deliberate,
 * not accidental. `test/traffic-health.test.ts` pins the values so the
 * drift is at least loud.
 */

/** Severity of one measured rate. Ordered `normal` < `warning` < `critical`. */
export type TrafficSeverity = 'normal' | 'warning' | 'critical';

/**
 * Error-rate thresholds.
 *
 * The warning arm carries a minimum sample size: 1 error out of 3
 * requests is 33% and means nothing. The critical arm deliberately has
 * no such floor and an absolute-count escape hatch — 25 failed requests
 * is worth waking up for at any denominator, and a low-volume upstream
 * that fails everything must not hide behind its own low volume.
 */
export const ERROR_RATE_WARNING = 0.02;
export const ERROR_RATE_CRITICAL = 0.1;
export const ERROR_RATE_WARNING_SAMPLE_MIN = 100;
export const ERROR_COUNT_CRITICAL = 25;

/**
 * Fallback-rate thresholds.
 *
 * Higher than the error thresholds on purpose: a fallback is the retry
 * ladder WORKING — the tenant was served. It is a supply-health signal
 * (the first-choice upstream is failing), not a tenant-facing outage, so
 * it warns later and criticals later.
 */
export const FALLBACK_RATE_WARNING = 0.05;
export const FALLBACK_RATE_CRITICAL = 0.2;

/** Slow-first-byte thresholds. */
export const SLOW_FIRST_BYTE_RATE_WARNING = 0.05;
export const SLOW_FIRST_BYTE_RATE_CRITICAL = 0.15;

/**
 * Grade an error rate.
 *
 * `count` is the absolute error count and `total` the request count the
 * rate was computed over; both arms need them, so callers must not pass
 * a rate alone.
 */
export function errorSeverity(rate: number, count: number, total: number): TrafficSeverity {
  if (rate >= ERROR_RATE_CRITICAL || count >= ERROR_COUNT_CRITICAL) return 'critical';
  if (rate >= ERROR_RATE_WARNING && total >= ERROR_RATE_WARNING_SAMPLE_MIN) return 'warning';
  return 'normal';
}

export function fallbackSeverity(rate: number): TrafficSeverity {
  if (rate >= FALLBACK_RATE_CRITICAL) return 'critical';
  if (rate >= FALLBACK_RATE_WARNING) return 'warning';
  return 'normal';
}

/**
 * Grade a slow-first-byte rate.
 *
 * ⚠ Fleet-level only. Do NOT grade a single SKU with this.
 *
 * "Slow" is a fixed global constant — `SLOW_FIRST_BYTE_MS = 10_000` in
 * `apps/gateway-worker/src/routes/internal-traffic.ts`. One 10-second ruler
 * across a catalogue whose inherent first-byte latency spans chat (~1s) to
 * image and video generation (~30-60s) is a category error the moment it is
 * applied per SKU: a media SKU is 100% "slow" by construction.
 *
 * Measured, production 24h, on SKUs with ZERO failures:
 *
 *   codex/gpt-image-2                44 req   88.6% slow   avg 34.8s
 *   kimi-code/k3                     40 req   62.5% slow   avg 20.6s
 *   claude-code-api/claude-sonnet-5   2 req   50.0% slow   avg  9.4s
 *
 * All three graded 严重 off this rule alone while every failure column read
 * 0.0%. The last one is 1 slow request out of 2 — this rule has no sample
 * floor, unlike both error rules.
 *
 * At fleet scale it still works, because the mix is dominated by chat and a
 * real latency regression moves the aggregate. That is the only place it is
 * used now. Grading a SKU needs a per-SKU baseline, which no surface
 * publishes; until one exists, latency is displayed, not judged.
 */
export function slowFirstByteSeverity(rate: number): TrafficSeverity {
  if (rate >= SLOW_FIRST_BYTE_RATE_CRITICAL) return 'critical';
  if (rate >= SLOW_FIRST_BYTE_RATE_WARNING) return 'warning';
  return 'normal';
}

/**
 * Grade a DOWNSTREAM SKU's failure rate — rate only, no absolute-count arm.
 *
 * `errorSeverity` criticals on 25 absolute failures at any denominator. That
 * arm is right where it came from: it stops a low-volume upstream that fails
 * everything from hiding behind its own denominator, and it grades the fleet
 * summary, where 25 failures is a real number.
 *
 * Applied per SKU it inverts. Measured on production's 24h window, graded
 * with the count arm in play:
 *
 *   xai/grok-4.6                  49 / 3,552   1.4%   → critical
 *   claude-code/claude-sonnet-5   40 / 2,539   1.6%   → critical
 *   codex/gpt-5.6-luna            50 / 1,701   2.9%   → critical
 *   openai/gpt-image-2           150 /   258  58.1%   → critical
 *
 * Every busy SKU is critical, so the one row that is actually broken looks
 * exactly like the four that are running normally. A board where everything
 * is red carries no signal — and the operator's whole reason for opening
 * this page is to find the row worth acting on.
 *
 * The low-volume case the count arm guarded is still covered here: the rate
 * arm's own sample floor withholds a WARNING below
 * `ERROR_RATE_WARNING_SAMPLE_MIN`, and a genuinely broken low-volume SKU
 * fails at a rate far above 10% anyway (gpt-image-2 at 58% criticals on rate
 * alone). What is given up is the case of a SKU with a large denominator and
 * exactly-25-ish failures at a low rate — which is the case we want quiet.
 *
 * Thresholds are `errorSeverity`'s, unchanged, so a rate that reads warning
 * on the traffic monitor reads warning here too.
 */
export function downstreamSeverity(rate: number, requests: number): TrafficSeverity {
  if (rate >= ERROR_RATE_CRITICAL) return 'critical';
  if (rate >= ERROR_RATE_WARNING && requests >= ERROR_RATE_WARNING_SAMPLE_MIN) return 'warning';
  return 'normal';
}

// ─── Supply health: a different question, a different counter ──────────
//
// Everything above grades the TENANT experience: every response >= 400.
// That is the wrong counter for "is this upstream broken", and the fleet
// already says so — `packages/contracts/src/internal-dashboard-health.ts`:
//
//   > only `status_code >= 500` counts, because this list answers "is the
//   > upstream broken?", not "what fraction of tenant requests succeeded?".
//   > 4xx rows lower the canonical success rate but do not indict an
//   > upstream.
//
// Production on 2026-09-01 is the case in point: channel
// `ndtpool-anthropic-oauth` ran 24h at a 41.0% error rate with 30 of 7,604
// errors being 5xx. The pool was fine; ~7,100 were admission-gate 403s from
// a single tenant retrying a CC-exclusive SKU through a bare SDK all night.
// Graded on total errors that channel is on fire. Graded on 5xx — the
// question actually being asked — it is at 0.16%.
//
// The thresholds below are the gateway's own, from
// `apps/gateway-worker/src/routes/internal-health.ts`
// (`MIN_REQUESTS_THRESHOLD` / `FAILURE_RATE_THRESHOLD`), which is what
// decides `unhealthy_upstreams_24h`. Same numbers so the console and the
// fleet cannot disagree about which upstream is broken.

/** Requests in the window below which a 5xx rate is not evidence. */
export const SUPPLY_MIN_REQUESTS = 20;
/** The fleet's unhealthy line: 20% 5xx. Authoritative. */
export const SUPPLY_FAILURE_RATE_CRITICAL = 0.2;
/**
 * Console-side early warning at half the fleet line.
 *
 * This one has NO authority behind it — the gateway defines exactly one
 * threshold and it is the critical one. It exists so a supply problem is
 * visible while it is forming rather than only once it has crossed the line
 * the fleet calls unhealthy. Only `SUPPLY_FAILURE_RATE_CRITICAL` means
 * "the fleet would list this upstream as unhealthy".
 */
export const SUPPLY_FAILURE_RATE_WARNING = 0.1;

/**
 * Grade SUPPLY health from 5xx alone.
 *
 * Below `SUPPLY_MIN_REQUESTS` this is always `normal`: one 5xx out of three
 * requests is not evidence that an upstream is broken, and unlike the
 * tenant-facing error rule there is no absolute-count escape hatch here,
 * because the gateway's own rule has none either.
 */
export function supplySeverity(serverErrorRate: number, requests: number): TrafficSeverity {
  if (requests < SUPPLY_MIN_REQUESTS) return 'normal';
  if (serverErrorRate >= SUPPLY_FAILURE_RATE_CRITICAL) return 'critical';
  if (serverErrorRate >= SUPPLY_FAILURE_RATE_WARNING) return 'warning';
  return 'normal';
}

/**
 * The supply-side failure figures (#2240), from the gateway's two dial
 * primitives — or `null` when this gateway does not carry them.
 *
 * Before #2240 the supply verdict was `serverErrorCount / requestCount`: 5xx
 * only. Two things it could not see, both measured on production:
 *
 *   - a pool 429 the retry ladder rescued. The gateway records the attempt
 *     under `<request_id>-a<n>`; excluded from the tenant counters (it is not
 *     a tenant request) it shows up ONLY as `upstreamAttemptFailureCount`;
 *   - an upstream that answered 401 / 402 / 403 / 429 on the final attempt.
 *     `xai/grok-4.6` ran 47/52 failures on upstream 402 ("usage balance
 *     exhausted") and the page read 上游失败率 0.0%.
 *
 *   dials    = requests − refusedBeforeUpstream + upstreamAttemptFailures
 *   failures = upstreamAttemptFailures + upstreamFinalFailures
 *
 * Every tenant request that REACHED an upstream is exactly one final dial —
 * a gate refusal never dialled, so it leaves the denominator the way it
 * leaves the downstream one (production 24h: a leg with 7,188 requests,
 * 7,175 of them refused, 3 failed dials read 0.04% on a 7,188 population;
 * the truth is 3/13). Every attempt failure is one EXTRA dial (the gateway
 * already drops the attempt row an exhausted chain writes for its own last
 * dial); a final dial fails at most once (`upstreamFinalFailures` requires
 * an upstream status, so it is disjoint from refusals and counts a request
 * once however many classification buckets it also lands in). So
 * `failures <= dials` holds structurally and nothing here is capped.
 * `serverErrors` and `upstreamRefused` are classification only and never
 * summed.
 *
 * Absence is UNKNOWN, not zero — for any of the three inputs, refusals
 * included: the caller falls back to the 5xx-only figure it had before,
 * which is a floor, and must not claim the counters are 0.
 */
export function supplyFailureFigures(input: {
  readonly requests: number;
  readonly refusedBeforeUpstream: number | null;
  readonly upstreamAttemptFailures: number | null;
  readonly upstreamFinalFailures: number | null;
}): { readonly dials: number; readonly failures: number; readonly rate: number } | null {
  if (
    input.refusedBeforeUpstream === null
    || input.upstreamAttemptFailures === null
    || input.upstreamFinalFailures === null
  ) {
    return null;
  }
  const dials = Math.max(0, input.requests - input.refusedBeforeUpstream) + input.upstreamAttemptFailures;
  const failures = input.upstreamAttemptFailures + input.upstreamFinalFailures;
  return { dials, failures, rate: dials > 0 ? failures / dials : 0 };
}

const SEVERITY_RANK: Readonly<Record<TrafficSeverity, number>> = {
  normal: 0,
  warning: 1,
  critical: 2,
};

/** Worst of several severities. Empty input is `normal`. */
export function worstSeverity(severities: readonly TrafficSeverity[]): TrafficSeverity {
  return severities.reduce<TrafficSeverity>(
    (worst, next) => (SEVERITY_RANK[next] > SEVERITY_RANK[worst] ? next : worst),
    'normal',
  );
}

// ─── Which refusals leave a failure rate ───────────────────────────────
//
// This lives beside the thresholds, not beside the page that first needed
// it, and the reason is a bug that shipped into review.
//
// `refusedBeforeUpstreamCount` is STRUCTURAL — every failure that never
// dialled an upstream. The gateway states what happened and takes no view
// on whose fault it was, correctly: baked in there it would be a judgement
// no consumer could revise. So the scalar is NOT "refusals that were not
// our fault". It contains `MODEL_NOT_AVAILABLE`, which never dialled an
// upstream precisely BECAUSE WE COULD NOT SERVE THE REQUEST — 349 in
// production's 24h window, 1,614 over 7d.
//
// When the classification lived under `pages/health-perspective/`, the
// traffic monitor could not reach it and deducted the raw scalar instead.
// The two surfaces then reported the same window as 0.5% and 2.0%, each
// with a test asserting the other was wrong — while the file claiming they
// could not disagree sat between them. A shared rule kept in a place only
// one caller can import is not shared; it is a copy waiting to happen.
//
// Fail-closed is the load-bearing property: a whitelist means a code
// introduced tomorrow counts against us until someone decides otherwise. A
// blacklist would absolve each new code the day it shipped and the number
// would quietly improve with nobody noticing.

/**
 * Refusals excluded from a downstream failure rate — the caller asked for
 * something we do not owe them and the system correctly said no.
 *
 *   REQUEST_NOT_SUPPORTED   the request is not one this SKU serves
 *   AUTHENTICATION_FAILED   the caller could not prove who they are
 *   INVALID_REQUEST         the payload was not a request we could parse
 *   INSUFFICIENT_BALANCE    the account has not paid for this
 *   ACCOUNT_UNAVAILABLE     the account is suspended or not serving
 *
 * Everything else is ours, including `'(uncoded)'` — "we do not know why we
 * refused this" is not an excuse — and any code added after this was
 * written. Membership is the whole content; edit it deliberately, because
 * the edit is a product decision about what we are answerable for.
 *
 * Never label any of this 防火墙 / admission-rejected. `REQUEST_NOT_SUPPORTED`
 * is the admission gate in practice, but the counter is structural and
 * neither surface has evidence of the mechanism.
 */
export const EXPECTED_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'REQUEST_NOT_SUPPORTED',
  'AUTHENTICATION_FAILED',
  'INVALID_REQUEST',
  'INSUFFICIENT_BALANCE',
  'ACCOUNT_UNAVAILABLE',
]);

/** Fail-closed: anything not on the list is counted against us. */
export function isExpectedRefusal(code: string): boolean {
  return EXPECTED_REFUSAL_CODES.has(code);
}

/**
 * How many refusals leave the ratio.
 *
 * `null` when the map is absent — it rides only on the window summary and
 * on per-SKU rows, and absence means "not reported at this grain", never
 * "none". An EMPTY map is a reported zero and returns 0, which is a
 * different and legitimate answer.
 *
 * A caller that cannot classify must exclude NOTHING rather than excluding
 * everything: only the second direction flatters us.
 */
export function expectedRefusalCount(
  byCode: Readonly<Record<string, number>> | null | undefined,
): number | null {
  if (byCode === null || byCode === undefined) return null;
  return Object.entries(byCode).reduce(
    (sum, [code, count]) => (count > 0 && isExpectedRefusal(code) ? sum + count : sum),
    0,
  );
}

/**
 * The denominator and numerator a downstream failure rate must use.
 *
 * A refused request never became our responsibility, so it leaves BOTH
 * sides. Netting only the numerator reads low, and worst exactly when
 * refusals dominate — 11/9,181 = 0.1% where the truth is 11/2,148 = 0.5%.
 *
 * Returns nulls when the split is unknowable, so a caller renders "—"
 * instead of a rate computed from a substituted zero.
 */
export function attributableFigures(
  requests: number,
  errors: number,
  byCode: Readonly<Record<string, number>> | null | undefined,
): { readonly requests: number | null; readonly errors: number | null; readonly rate: number | null } {
  const excluded = expectedRefusalCount(byCode);
  if (excluded === null) return { requests: null, errors: null, rate: null };
  const attributableRequests = Math.max(0, requests - excluded);
  const attributableErrors = Math.max(0, errors - excluded);
  return {
    requests: attributableRequests,
    errors: attributableErrors,
    rate: attributableRequests > 0 ? attributableErrors / attributableRequests : 0,
  };
}
